import React from "react";
import { Box, Collapse, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { RewardDetail } from "@/utils/api/referrals";
import { RewardCard } from "./RewardCard";

interface ExpandableRewardDetailsProps {
  isExpanded: boolean;
  referrerRewards: RewardDetail[];
  refereeRewards: RewardDetail[];
  totalRewardAmount: number;
}

/**
 * Component for displaying expandable reward details section
 */
export const ExpandableRewardDetails: React.FC<
  ExpandableRewardDetailsProps
> = ({ isExpanded, referrerRewards, refereeRewards, totalRewardAmount }) => {
  return (
    <Collapse in={isExpanded}>
      <Box p="md">
        <Stack gap="md">
          <Text size="sm" fw={600} c="dimmed">
            Reward Details
          </Text>

          {/* Your Rewards */}
          {referrerRewards.length > 0 && (
            <Box>
              <Group gap={4} mb="xs">
                <Text size="xs" c="dimmed" fw={500}>
                  Your Rewards
                </Text>
                <Tooltip
                  label="All reward payments you've earned from this referral"
                  multiline
                  w={200}
                >
                  <IconInfoCircle
                    size={10}
                    style={{
                      color: "var(--mantine-color-dimmed)",
                    }}
                  />
                </Tooltip>
              </Group>
              <Stack gap="xs">
                {referrerRewards.map((reward) => (
                  <RewardCard key={reward.id} reward={reward} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Their Rewards */}
          {refereeRewards.length > 0 && (
            <Box>
              <Group gap={4} mb="xs">
                <Text size="xs" c="dimmed" fw={500}>
                  Their Rewards
                </Text>
                <Tooltip
                  label="All reward payments the person you referred received"
                  multiline
                  w={200}
                >
                  <IconInfoCircle
                    size={10}
                    style={{
                      color: "var(--mantine-color-dimmed)",
                    }}
                  />
                </Tooltip>
              </Group>
              <Stack gap="xs">
                {refereeRewards.map((reward) => (
                  <RewardCard key={reward.id} reward={reward} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Total Reward Amount */}
          <Group justify="space-between">
            <Group gap={4}>
              <Text size="sm" c="dimmed">
                Total Reward Pool:
              </Text>
              <Tooltip
                label="The combined total of both your reward and their reward for this referral"
                multiline
                w={200}
              >
                <IconInfoCircle
                  size={10}
                  style={{ color: "var(--mantine-color-dimmed)" }}
                />
              </Tooltip>
            </Group>
            <Text size="sm" fw={600}>
              ${totalRewardAmount.toFixed(2)}
            </Text>
          </Group>
        </Stack>
      </Box>
    </Collapse>
  );
};
